// Globalny stan aplikacji - chat
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

 type ChatMode = 'docked' | 'horizontal' | 'floating' | null;
 type ChatModeWithFullscreen = 'docked' | 'horizontal' | 'floating' | 'fullscreen' | null

// typ chat
export interface IChatSlice {
  isAgentAiVisible: boolean;
  isChatOpen: boolean;
  isChatDocked: boolean;
  isChatHorizontal: boolean;
  isChatHeaderVisible: boolean;
  chatDockedWidth: number;
  chatHorizontalHeight: number;
  chatFloatingWidth: number;
  chatFloatingHeight: number;
  isChatResizable: boolean;
  isChatFullScreen: boolean;
  lastChatModeBeforeFullscreen: ChatMode;
  lastChatModeBeforeClose: ChatModeWithFullscreen;
  chatThreadLost: boolean;
  devStudioContext: { processId: string | null; workflowId: string | null };
}

// dane inicjalizacyjne
const initialState: IChatSlice = {
  isAgentAiVisible: true,
  isChatOpen: false,
  isChatDocked: false,
  isChatHorizontal: false,
  isChatHeaderVisible: true,
  chatDockedWidth: 400,
  chatHorizontalHeight: 600,
  chatFloatingWidth: 400,
  chatFloatingHeight: 650,
  isChatResizable: false,
  isChatFullScreen: false,
  lastChatModeBeforeFullscreen: null,
  lastChatModeBeforeClose: null,
  chatThreadLost: false,
  devStudioContext: { processId: null, workflowId: null }
};

// Funkcje pomocnicze do zarządzania trybami chatu
const getCurrentChatMode = (state: IChatSlice): ChatMode => {
  if (state.isChatDocked) return 'docked';
  if (state.isChatHorizontal) return 'horizontal';
  if (state.isChatResizable) return 'floating';
  return null;
};

const setChatMode = (state: IChatSlice, mode: ChatMode | ChatModeWithFullscreen) => {
  // Wyłącz wszystkie tryby
  state.isChatDocked = false;
  state.isChatHorizontal = false;
  state.isChatResizable = false;
  state.isChatFullScreen = false;
  
  // Włącz odpowiedni tryb
  if (mode === 'docked') {
    state.isChatDocked = true;
  } else if (mode === 'horizontal') {
    state.isChatHorizontal = true;
  } else if (mode === 'floating') {
    state.isChatResizable = true;
  } else if (mode === 'fullscreen') {
    state.isChatFullScreen = true;
  }
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setIsAgentAiVisible: (state, action: PayloadAction<IChatSlice['isAgentAiVisible']>) => {
      state.isAgentAiVisible = action.payload;
    },
    setIsChatOpen: (state, action: PayloadAction<IChatSlice['isChatOpen']>) => {
      const wasOpen = state.isChatOpen;
      const isOpening = action.payload && !wasOpen;
      const isClosing = !action.payload && wasOpen;
      
      state.isChatOpen = action.payload;
      
      // Zapamiętaj tryb przed zamknięciem
      if (isClosing) {
        if (state.isChatFullScreen) {
          state.lastChatModeBeforeClose = 'fullscreen';
        } else {
          state.lastChatModeBeforeClose = getCurrentChatMode(state);
        }
      }
      
      // Przywróć ostatni tryb przy otwarciu
      if (isOpening && state.lastChatModeBeforeClose) {
        setChatMode(state, state.lastChatModeBeforeClose);
        state.lastChatModeBeforeClose = null;
      }
    },
    setIsChatDocked: (state, action: PayloadAction<IChatSlice['isChatDocked']>) => {
      state.isChatDocked = action.payload;
    },
    setIsChatHorizontal: (state, action: PayloadAction<IChatSlice['isChatHorizontal']>) => {
      state.isChatHorizontal = action.payload;
    },
    setIsChatHeaderVisible: (state, action: PayloadAction<IChatSlice['isChatHeaderVisible']>) => {
      state.isChatHeaderVisible = action.payload;
    },
    setChatDockedWidth: (state, action: PayloadAction<IChatSlice['chatDockedWidth']>) => {
      state.chatDockedWidth = action.payload;
    },
    setChatHorizontalHeight: (state, action: PayloadAction<IChatSlice['chatHorizontalHeight']>) => {
      state.chatHorizontalHeight = action.payload;
    },
    setChatFloatingWidth: (state, action: PayloadAction<IChatSlice['chatFloatingWidth']>) => {
      state.chatFloatingWidth = action.payload;
    },
    setChatFloatingHeight: (state, action: PayloadAction<IChatSlice['chatFloatingHeight']>) => {
      state.chatFloatingHeight = action.payload;
    },
    setIsChatResizable: (state, action: PayloadAction<IChatSlice['isChatResizable']>) => {
      state.isChatResizable = action.payload;
    },
    setChatFullScreen: (state, action: PayloadAction<IChatSlice['isChatFullScreen']>) => {
      const isEnablingFullscreen = action.payload && !state.isChatFullScreen;
      
      if (isEnablingFullscreen) {
        state.lastChatModeBeforeFullscreen = getCurrentChatMode(state);
        // Wyłącz inne tryby
        state.isChatDocked = false;
        state.isChatHorizontal = false;
        state.isChatResizable = false;
      }
      
      state.isChatFullScreen = action.payload;
    },
    restoreLastChatModeBeforeFullscreen: (state) => {
      const modeToRestore = state.lastChatModeBeforeFullscreen;
      setChatMode(state, modeToRestore);
      state.lastChatModeBeforeFullscreen = null;
    },
    setChatModeDirect: (state, action: PayloadAction<ChatMode | ChatModeWithFullscreen>) => {
      setChatMode(state, action.payload);
    },
    setChatThreadLost: (state, action: PayloadAction<IChatSlice['chatThreadLost']>) => {
      state.chatThreadLost = action.payload;
    },
    setDevStudioContext: (state, action: PayloadAction<{ processId: string | null; workflowId: string | null }>) => {
      state.devStudioContext = action.payload;
    },
  }
});

export const chatSliceActions = chatSlice.actions;

export default chatSlice.reducer;
